import { useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Mail, Lock, Loader2, ShieldCheck } from "lucide-react";
import { useAuth, formatApiErrorDetail } from "../context/AuthContext";
import { LOGO } from "../lib/site";

const inputCls =
  "w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3.5 pl-11 text-sm text-slate-100 placeholder:text-slate-500 outline-none focus:border-cyan-400/60 focus:bg-cyan-400/5 focus:shadow-[0_0_20px_rgba(0,240,255,0.12)] transition-[border-color,background-color,box-shadow] duration-300";

export default function AdminLogin() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/admin" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/admin");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#07090E] circuit-grid flex items-center justify-center px-4" data-testid="admin-login-page">
      <div className="absolute top-1/3 left-1/2 -translate-x-1/2 h-[420px] w-[420px] rounded-full bg-cyan-500/10 blur-[140px] pointer-events-none" />
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className="glow-card relative w-full max-w-md rounded-2xl p-8 sm:p-10"
      >
        <div className="flex flex-col items-center text-center">
          <span className="flex h-14 w-14 items-center justify-center rounded-xl bg-white/95 ring-1 ring-cyan-400/40 shadow-[0_0_28px_rgba(0,240,255,0.25)]">
            <img src={LOGO} alt="TalkLogical logo" className="h-10 w-10 object-contain" />
          </span>
          <h1 className="mt-5 font-display text-2xl font-bold tracking-tight text-slate-50">
            Leads Dashboard
          </h1>
          <p className="mt-2 flex items-center gap-2 text-xs font-mono text-slate-500">
            <ShieldCheck className="h-3.5 w-3.5 text-emerald-400" />
            Authorized TalkLogical admins only
          </p>
        </div>

        <form onSubmit={submit} className="mt-8 space-y-5" data-testid="admin-login-form">
          <div className="relative">
            <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500 pointer-events-none" />
            <input
              data-testid="admin-login-input-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="admin@talklogical.com"
              className={inputCls}
            />
          </div>
          <div className="relative">
            <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500 pointer-events-none" />
            <input
              data-testid="admin-login-input-password"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              className={inputCls}
            />
          </div>

          {error && (
            <p data-testid="admin-login-error" className="rounded-xl border border-red-400/30 bg-red-400/10 px-4 py-3 text-xs text-red-300">
              {error}
            </p>
          )}

          <button
            data-testid="admin-login-submit-button"
            type="submit"
            disabled={loading}
            className="w-full inline-flex items-center justify-center gap-2.5 rounded-xl bg-cyan-400 px-6 py-3.5 text-sm font-bold text-[#07090E] hover:bg-cyan-300 hover:shadow-[0_0_36px_rgba(0,240,255,0.4)] disabled:opacity-60 transition-[background-color,box-shadow] duration-300"
          >
            {loading && <Loader2 className="h-4 w-4 animate-spin" />}
            {loading ? "Signing in…" : "Sign in"}
          </button>
        </form>

        <Link
          to="/"
          data-testid="admin-login-back-link"
          className="mt-6 block text-center text-xs text-slate-500 hover:text-cyan-300 transition-colors"
        >
          ← Back to talklogical.com
        </Link>
      </motion.div>
    </div>
  );
}
